import { Component, Vue } from 'vue-property-decorator';
import {
	loadStripe,
	Stripe,
	StripeCardElement,
	StripeIbanElement,
	PaymentMethod,
	StripeError
} from '@stripe/stripe-js';
import { ValidationObserver } from 'vee-validate';
import template from './Add.vue';

import Button from '@/components/Button';
import MDTextFieldWithValidation from '@/components/Validation/MDTextFieldWithValidation';
import store from '@/store';
import auth from '@/store/modules/auth';
import { PaymentForm, PaymentType, PaymentFormSEPA, Payment, PaymentAction } from '@/store/models/payment';
import payment from '@/store/modules/payment';
import { ErrorFrontend, router, validation } from '@/_helpers';
import BackButton from '@/components/BackButton';
import { env } from '@/_helpers/env';

@Component({
	mixins: [template],
	components: {
		ValidationObserver,
		Button,
		MDTextFieldWithValidation,
		BackButton
	}
})
export default class PaymentAdd extends Vue {
	loading: boolean = false;

	setting: PaymentType = PaymentType.CARD;

	stripe: Stripe | null = null;

	cardElement: StripeCardElement | null = null;

	sepaElement: StripeIbanElement | null = null;

	isDefault: boolean = false;

	sepaAccountHolderName: string = '';

	alertShow: boolean = false;

	alertTxt: string = '';

	form: PaymentForm = {
		stripeId: '',
		customerId: auth.self.customerId,
		type: this.setting,
		additionalInformation: {},
		isDefault: true,
		billingDetails: {
			name: '',
			email: ''
		}
	};

	ccErrors: ErrorFrontend = {
		cc_validation: []
	};

	sepaErrors: ErrorFrontend = {
		sepa_validation: []
	};

	async mounted(): Promise<void> {
		this.loading = true;

		if (env.STRIPE_KEY !== undefined) {
			this.stripe = await loadStripe(env.STRIPE_KEY);
		}

		// Get self + customerId
		const self = await auth.getSelf(false);
		if (self) this.form.customerId = self.data.customerId;

		const elements = this.stripe?.elements();

		if (elements !== undefined) {
			this.cardElement = elements.create('card', {
				hidePostalCode: true
			});
			this.cardElement.on('change', (event) => {
				this.ccErrors.cc_validation = [];
				if (event.error) {
					this.ccErrors.cc_validation.push(event.error.message);
				}
				(this.$refs.formData as any).setErrors(this.ccErrors);
			});
			this.cardElement.mount('#card-element');

			this.sepaElement = elements.create('iban', {
				supportedCountries: ['SEPA']
			});
			this.sepaElement.mount('#sepa-element');
			this.sepaElement.on('change', (event) => {
				this.sepaErrors.sepa_validation = [];
				if (event.error) {
					this.sepaErrors.sepa_validation.push(event.error.message);
				}
				(this.$refs.formData as any).setErrors(this.sepaErrors);
			});
		} else {
			store.commit('openNotifier', { text: `Er ging wat mis tijdens het laden`, color: 'error' });
		}

		this.loading = false;
	}

	setSetting(setting: PaymentType): void {
		this.setting = setting;
	}

	async add(): Promise<void> {
		const isValid = await (this.$refs.formData as any).validate();

		if (this.setting === PaymentType.CARD) {
			// Is necessary to force the stripe errors to not been thrown away
			(this.$refs.formData as any).setErrors(this.ccErrors);
		}

		if (this.setting === PaymentType.SEPA) {
			// Is necessary to force the stripe errors to not been thrown away
			(this.$refs.formData as any).setErrors(this.sepaErrors);
		}

		if (!isValid) {
			return;
		}

		this.loading = true;
		this.alertShow = false;
		this.alertTxt = '';

		let payMethod: PaymentMethod | undefined;
		let err: StripeError | undefined;

		if (this.stripe !== null) {
			if (this.setting === PaymentType.CARD && this.cardElement !== null) {
				const { paymentMethod, error } = await this.stripe.createPaymentMethod({
					type: PaymentType.CARD,
					card: this.cardElement,
					billing_details: {
						name: this.form.billingDetails.name
					}
				});
				payMethod = paymentMethod;
				err = error;
			} else if (this.setting === PaymentType.SEPA && this.sepaElement !== null) {
				const { paymentMethod, error } = await this.stripe.createPaymentMethod({
					type: PaymentType.SEPA,
					sepa_debit: this.sepaElement,
					billing_details: {
						name: this.sepaAccountHolderName,
						email: this.form.billingDetails.email
					}
				});
				payMethod = paymentMethod;
				err = error;
			} else {
				store.commit('openNotifier', { text: `Er ging wat mis tijdens het opslaan`, color: 'error' });
			}

			if (payMethod !== undefined && err === undefined) {
				const sepa: PaymentFormSEPA = {};
				if (this.setting === PaymentType.CARD) {
					this.form.additionalInformation.card = {
						last4: payMethod.card?.last4,
						// eslint-disable-next-line camelcase
						experationMonth: payMethod.card?.exp_month,
						// eslint-disable-next-line camelcase
						experationYear: payMethod.card?.exp_year
					};
				} else if (this.setting === PaymentType.SEPA) {
					this.form.additionalInformation.sepaDebit = {
						last4: payMethod.sepa_debit?.last4,
						// eslint-disable-next-line camelcase
						bankCode: payMethod.sepa_debit?.bank_code,
						country: payMethod.sepa_debit?.country
					};
				}

				this.form.stripeId = payMethod.id;
				this.form.type = this.setting;

				try {
					const response = await payment.create(this.form);
					const action: PaymentAction = response.data as PaymentAction;
					let isRedirecting: Boolean = false;
					// If response contains redirect to URL

					if (action) {
						if (action.type === 'redirect_to_url') {
							if (action.actionRedirect) {
								window.location.href = action.actionRedirect?.url;
								isRedirecting = true;
							}
						}
					}

					if (!isRedirecting) {
						router.push({ name: 'payment' });
					}
				} catch (error) {
					const errors = validation.getErrors(error.data.errors);

					if (Object.keys(errors).length === 0 && errors.constructor === Object) {
						store.commit('openNotifier', { text: `Er ging wat mis tijdens het opslaan!`, color: 'error' });
					} else if (error.data.errors[0].source.pointer === 'stripe') {
						this.alertShow = true;
						this.alertTxt = error.data.errors[0].detail;
					} else {
						(this.$refs.formData as any).setErrors(errors);
					}
				} finally {
					this.loading = false;
				}
			}
		}

		this.loading = false;
	}
}
